import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../../../variable-overrides'
import type * as aliases from '../../../../../aliases'
import type * as currentRendering from '../../../feature'
import * as models from '../../../models'
import type * as actions from '../../actions'
import type { Feature } from '../feature'

/**
 * Re-renders {@link ents.core.mosaic.Widget} when an action completes successfully.
 * Looks for widgets where the {@link ents.core.mosaic.Action.Id} is in their
 * {@link models.widget.Rendering} list.
 */

export const reRenderWatchedWidgets = store.effects.create(
  (
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
    actionsFeature: actions.Feature,
    variablesFeature: variableOverrides.Feature,
    aliasesFeature: aliases.Feature,
  ) =>
    actionsFeature.actions.submitAction.success.observe().pipe(
      rxjs.switchMap(action => {
        const {
          data: { response: executionResult },
        } = action
        // Find all widget IDs that are monitoring this action
        return feature.selectors
          .getRefsWatching(executionResult)
          .asObservable.pipe(
            // Take only the latest value of widget IDs
            rxjs.take(1),
            // Transform ID collection into individual ID stream entries
            rxjs.switchMap(refs => rxjs.from(refs)),
            // Join widget ID with current rendering context
            rxjs.withLatestFrom(
              currentRenderingFeature.selectors.context.asObservable,
            ),
            rxjs.switchMap(([ref, pageContext]) => {
              // If we dont have page context, we cannot render the widget
              if (pageContext === undefined) {
                return rxjs.EMPTY
              }

              return (
                aliasesFeature.selectors
                  // Convert widget reference (by ID or alias) to its actual widget ID
                  .getWidgetId(ref)
                  .asObservable.pipe(
                    rxjs.switchMap(
                      // base don the widget id try obtaining widget
                      // variables
                      widgetId =>
                        variablesFeature.selectors.getVariableOverrides(
                          widgetId
                            ? {
                                widget: {
                                  appId: pageContext.appId,
                                  pageId: pageContext.pageId,
                                  refId: ref.id,
                                  scenarioId: pageContext.scenarioId,
                                  widgetId,
                                },
                              }
                            : // in case widget id is undefined, get
                              // at last other variables (page,
                              // session etc...)
                              undefined,
                        ).asObservable,
                    ),
                    // even thought the above selectors should
                    // deduplicate most requests there is edge
                    // case where we start with no widget id, then
                    // get the widget id, but end up with the same
                    // variables
                    rxjs.distinctUntilChanged(isDeepEqual),
                    // We are only interested in the latest value of the
                    // variables when we are doing rerendering
                    rxjs.take(1),
                    rxjs.map(variables =>
                      // Create render request for given widget
                      feature.actions.render.from(action).create({
                        pageContext,
                        ref,
                        variables,
                      }),
                    ),
                  )
              )
            }),
          )
      }),
    ),
)
